import { useMemo } from 'react';
import { UserProfile } from '../types';
import { isUserActiveSubstitute } from '../services/substitutionService';
import { getProjectRelation, ProjectAccessRow } from '../services/projectAccess';
import { sameCompany, CompanyScoped } from '../services/companyScope';
import type { RechteSicht } from '../services/rechte/rechteService';
import type { ProjectStatusSource } from '../services/projectBilling';
import type { ListProject } from '../services/listProject';

/**
 * Was Projektliste und Karte von den Rechten brauchen (Rollensystem,
 * docs/rollensystem-plan.md §4): das Recht `projekte.alle_sehen` und, ob die
 * Rechte schon aus der Datenbank kommen. In der Anwendung ist das die Sicht
 * aus `useRechte`; Tests bauen sie mit `rechteSicht`, ohne React.
 */
export type ListenRechte = Pick<RechteSicht, 'hat' | 'quelle'>;

const istAdminRolle = (role: string | undefined | null): boolean =>
    role === 'admin' || role === 'super_admin';

/**
 * Sieht dieser Nutzer in der PERSÖNLICHEN Projektliste alle Projekte, ohne
 * Zuständigkeitsprüfung und ohne Schalter?
 *
 * Nur noch im RÜCKFALL des Rollensystems: solange die Rechte nicht aus der
 * Datenbank kommen (Migration offen, Antwort steht noch aus), bleibt die
 * Arbeitsvorbereitung beim bisherigen Verhalten — sie plant über den
 * gesamten Bestand und sah immer alles. Kommen die Rechte aus der Datenbank,
 * gibt es kein Zwangs-„alles" mehr: wer `projekte.alle_sehen` hat, bekommt
 * den Schalter „Alle" (`canShowAllProjects`), ab Werk aus.
 *
 * Bewusst NICHT im Gleichlauf mit `public.is_admin()`
 * (supabase/rls_hardening.sql): 'admin' und 'super_admin' fehlen hier
 * absichtlich. Die persönliche Liste ist die Arbeitsliste des Nutzers und
 * zeigt auch einem Admin nur, wo er Besitzer, Hauptverantwortlicher oder
 * aktiver Vertreter ist. Die Gesamtsicht über alle Projekte liegt in der
 * Projektverwaltung im Admin-Bereich (components/admin/AdminProjectList.tsx)
 * und — seit 25.09.2026, weil die Projektverwaltung mobil fehlt — hinter dem
 * Chip „Alle" (siehe `canShowAllProjects`), den man selbst einschaltet.
 * Die Vorgabe bleibt die Arbeitsliste. Der ZUGRIFF bleibt davon unberührt —
 * Admins öffnen weiterhin jedes Projekt und arbeiten darin.
 *
 * Als exportierte reine Funktion, damit die Regel ohne DOM-Testumgebung
 * prüfbar ist (kein jsdom im Projekt).
 */
export const seesAllProjects = (rechte: ListenRechte, role: string | undefined | null): boolean =>
    rechte.quelle === 'systemrolle' && role === 'arbeitsvorbereitung';

/**
 * Darf dieser Nutzer in der Projektliste auf „Alle" umschalten?
 *
 * Jeder mit dem Recht `projekte.alle_sehen` — admin und super_admin haben es
 * immer, alle anderen über ihre Rollen. Ausgenommen ist nur die
 * Arbeitsvorbereitung im Rückfall (`seesAllProjects`): sie sieht ohnehin
 * alles. Freelancer haben nie Rechte und bekommen den Schalter nie, auch
 * nicht über einen gemerkten Wert aus einer früheren Rolle.
 */
export const canShowAllProjects = (rechte: ListenRechte, role: string | undefined | null): boolean =>
    rechte.hat('projekte.alle_sehen') && !seesAllProjects(rechte, role);

/**
 * Ist „Alle" für diesen Nutzer WIRKLICH an? Der gemerkte Wunsch zählt nur,
 * solange das Recht ihn trägt — wer es verliert, fällt still auf die
 * persönliche Liste zurück.
 */
export const resolveShowAll = (
    requested: boolean | undefined | null,
    rechte: ListenRechte,
    role: string | undefined | null,
): boolean =>
    !!requested && canShowAllProjects(rechte, role);

/** Profilzeile, soweit die Firmenzuordnung sie braucht. */
export interface ScopeUser extends CompanyScoped {
    id: string;
}

type ScopeViewer = (CompanyScoped & { id?: string | null; role?: string | null }) | null | undefined;

/**
 * Der Bereich hinter „Alle": super_admin alles, was der Client geladen hat;
 * jeder andere nur Projekte, deren Besitzer (`user_id`) laut `sameCompany`
 * zur eigenen Firma gehört. Ob der Nutzer „Alle" überhaupt darf, prüft der
 * Aufrufer (`selectAllScope`, `filterAdminScope`).
 *
 * `null` heißt „nicht bestimmbar": kein Profil, oder die Benutzerliste fehlt
 * noch (lädt, Abruf gescheitert). Der Aufrufer entscheidet dann selbst,
 * statt eine leere Liste für die Wahrheit zu halten.
 */
export const filterAllScope = <T extends { user_id?: string | null }>(
    projects: T[],
    userProfile: ScopeViewer,
    users: ScopeUser[] | null | undefined
): T[] | null => {
    if (!userProfile || !userProfile.id) return null;
    if (userProfile.role === 'super_admin') return projects;
    if (!users) return null;
    const companyUserIds = new Set(users.filter(u => sameCompany(u, userProfile)).map(u => u.id));
    return projects.filter(p => !!p.user_id && companyUserIds.has(p.user_id));
};

/**
 * Die Projekte im Verwaltungsbereich eines Admins — Regel wortgleich mit
 * `filteredProjects` in components/AdminView.tsx: super_admin alles, was der
 * Client geladen hat; admin nur Projekte, deren Besitzer (`user_id`) laut
 * `sameCompany` zur eigenen Firma gehört.
 *
 * `null` heißt „nicht bestimmbar": keine Admin-Rolle, oder für einen admin
 * fehlt die Benutzerliste noch (lädt, Abruf gescheitert). Der Aufrufer
 * entscheidet dann selbst, statt eine leere Liste für die Wahrheit zu halten.
 */
export const filterAdminScope = <T extends { user_id?: string | null }>(
    projects: T[],
    userProfile: ScopeViewer,
    users: ScopeUser[] | null | undefined
): T[] | null => {
    if (!userProfile || !userProfile.id) return null;
    if (!istAdminRolle(userProfile.role)) return null;
    return filterAllScope(projects, userProfile, users);
};

/**
 * Projekte hinter dem Chip „Alle" der Projektliste: für admin und
 * super_admin der Verwaltungsbereich wie bisher, für alle anderen mit dem
 * Recht die eigene Firma (dieselbe Regel). `null` = kein Recht auf „Alle"
 * oder der Bereich ist (noch) nicht bestimmbar.
 */
export const selectAllScope = <T extends { user_id?: string | null }>(
    projects: T[],
    userProfile: ScopeViewer,
    rechte: ListenRechte,
    users: ScopeUser[] | null | undefined
): T[] | null => {
    if (!canShowAllProjects(rechte, userProfile?.role)) return null;
    return filterAllScope(projects, userProfile, users);
};

/**
 * Projekte der Karte unter „Alle" (components/GlobalMapDashboard.tsx).
 *
 * admin und super_admin sahen dort schon immer alles, was geladen ist, die
 * Arbeitsvorbereitung ebenso — das bleibt (die Arbeitsvorbereitung nur im
 * Rückfall, siehe `seesAllProjects`). Wer das Recht über eine Rolle hat,
 * sieht wie in der Projektliste die eigene Firma.
 *
 * `null` = kein Recht auf „Alle" oder der Bereich ist noch nicht bestimmbar
 * (Benutzerliste fehlt) — die Karte bleibt dann bei „Meine".
 */
export const selectMapAllProjects = <T extends { user_id?: string | null }>(
    projects: T[],
    userProfile: ScopeViewer,
    rechte: ListenRechte,
    users: ScopeUser[] | null | undefined
): T[] | null => {
    if (!userProfile || !userProfile.id) return null;
    if (!rechte.hat('projekte.alle_sehen')) return null;
    if (istAdminRolle(userProfile.role) || seesAllProjects(rechte, userProfile.role)) return projects;
    return filterAllScope(projects, userProfile, users);
};

export type ProjectStatusFilter = 'all' | 'active' | 'planning' | 'on_hold' | 'completed' | 'finished' | 'active_all';

/** Status eines Listeneintrags; ohne Angabe gilt 'planning'. */
const statusOf = (p: ProjectStatusSource | null | undefined): string => p?.status || p?.data?.metadata?.globalData?.status || 'planning';

/** Passt ein Projekt zum Status-Chip? 'active_all' = alles außer Archiv. */
export const matchesStatusFilter = (p: ProjectStatusSource | null | undefined, statusFilter: ProjectStatusFilter): boolean => {
    if (statusFilter === 'all') return true;
    const status = statusOf(p);
    if (statusFilter === 'active_all') return status !== 'finished';
    return status === statusFilter;
};

/** Sicht-Schalter der persönlichen Liste; fehlt ein Schalter, ist er an. */
export interface PersonalListFilters {
    showOwn?: boolean;
    showSubstitute?: boolean;
    showDeputy?: boolean;
}

/**
 * Gehört dieses Projekt in die PERSÖNLICHE Liste dieses Nutzers?
 *
 * Ausgelagert aus dem Hook, damit die Regel ohne DOM-Testumgebung prüfbar
 * bleibt (Standing Decision: Logik in puren Funktionen, kein jsdom).
 */
export const isVisibleInPersonalList = (
    project: ProjectAccessRow | undefined | null,
    userProfile: { id?: string | null; email?: string | null; role?: string | null } | null | undefined,
    rechte: ListenRechte,
    viewFilters: PersonalListFilters = {}
): boolean => {
    if (!userProfile || !userProfile.id) return false;
    if (seesAllProjects(rechte, userProfile.role)) return true;

    const showOwn = viewFilters.showOwn !== false;
    const showSubstitute = viewFilters.showSubstitute !== false;
    const showDeputy = viewFilters.showDeputy !== false;

    // Eine Quelle für alle Sichtbarkeitsprüfungen (services/projectAccess.ts).
    const rel = getProjectRelation(project, userProfile);

    // 1. Ownership — Position 0 zählt mit: Alt-Projekte tragen keine
    // `coordinatorId`, ihr Hauptverantwortlicher fiel sonst aus diesem
    // Eimer heraus und wurde unten als „Vertreter" eingesammelt.
    const isPrimary = rel.isOwner || rel.isCoordinator || rel.isPrimaryRepresentative;
    if (isPrimary && showOwn) return true;

    // 2. Substitution — Array-Sicht, prüft Start- UND Enddatum
    if (rel.isSubstitute && showSubstitute) return true;

    // 3. Weitere Verantwortliche: nur echte Stellvertretung (Position > 0).
    if (rel.isDeputyListed && showDeputy) return true;

    return false;
};

/**
 * Welche (bereits entdoppelten) Projekte stehen in der Liste, bevor Suche,
 * Status und Sortierung greifen?
 *
 *   1. Arbeitsvorbereitung im Rückfall → alles (`seesAllProjects`, „Alle"
 *      spielt keine Rolle).
 *   2. „Alle" an UND Recht `projekte.alle_sehen` → `selectAllScope`: admin
 *      und super_admin der Verwaltungsbereich, alle anderen die eigene Firma.
 *      Fehlt die Benutzerliste noch, bleibt die persönliche Liste stehen —
 *      besser als kurz „Keine Projekte".
 *   3. sonst → persönliche Liste (`isVisibleInPersonalList`).
 */
export const selectRelevantProjects = <T extends ProjectAccessRow>(
    uniqueProjects: T[],
    userProfile: (CompanyScoped & { id?: string | null; email?: string | null; role?: string | null }) | null | undefined,
    rechte: ListenRechte,
    viewFilters: PersonalListFilters = {},
    options: { showAll?: boolean; scopeUsers?: ScopeUser[] | null } = {}
): T[] => {
    if (!userProfile || !userProfile.id) return [];
    // Vollzugriff ohne Schalter nur noch im Rückfall — alle anderen,
    // Admins eingeschlossen, durchlaufen hier bewusst die normale
    // Zuordnungsprüfung, solange sie nicht selbst „Alle" gewählt haben.
    if (seesAllProjects(rechte, userProfile.role)) return uniqueProjects;

    if (resolveShowAll(options.showAll, rechte, userProfile.role)) {
        const scope = selectAllScope(uniqueProjects, userProfile, rechte, options.scopeUsers);
        if (scope) return scope;
    }

    return uniqueProjects.filter(p => isVisibleInPersonalList(p, userProfile, rechte, viewFilters));
};

interface UseProjectDataProps {
    projects: ListProject[];
    userProfile: UserProfile | null;
    searchTerm: string;
    statusFilter: ProjectStatusFilter;
    viewFilters: {
        showOwn: boolean;
        showSubstitute: boolean;
        showDeputy?: boolean;
    };
    sortConfig: { key: string; direction: 'asc' | 'desc' };
    /** Rechte des Betrachters (`useRechte`). */
    rechte: ListenRechte;
    /** Chip „Alle" gewählt. Wirkt nur mit dem Recht `projekte.alle_sehen` (`resolveShowAll`). */
    showAll?: boolean;
    /** Profile für den Firmenbereich; super_admin braucht sie nicht. */
    scopeUsers?: ScopeUser[] | null;
}

export const useProjectData = ({
    projects, userProfile, searchTerm, statusFilter, viewFilters, sortConfig, rechte, showAll = false, scopeUsers = null
}: UseProjectDataProps) => {

    // 1. Deduplicate
    const uniqueProjects = useMemo(() => {
        const groups: Record<string, ListProject> = {};
        projects.forEach(p => {
            if (!p || !p.data || !p.data.metadata) return;
            const pNumber = p.project_number || p.data.metadata.projectNumber;
            const meta = p.data.metadata;
            const globalData = meta.globalData;
            let key = '';
            if (pNumber && typeof pNumber === 'string' && pNumber.trim().length > 0) key = `num_${pNumber.trim()}`;
            else if (globalData?.projectId && globalData.projectId !== 'temp-id') key = `uuid_${globalData.projectId}`;
            else key = `id_${p.id}`;
            if (!groups[key]) { groups[key] = p; }
            else {
                const currentNr = meta?.inspectionNumber || 0;
                const existingNr = groups[key].data.metadata?.inspectionNumber || 0;
                if (currentNr > existingNr) groups[key] = p;
                else if (currentNr === existingNr && new Date(p.updated_at) > new Date(groups[key].updated_at)) groups[key] = p;
            }
        });
        return Object.values(groups);
    }, [projects]);

    // 2. Filter by Ownership (oder Verwaltungsbereich bei „Alle")
    const relevantProjects = useMemo(
        () => selectRelevantProjects(uniqueProjects, userProfile, rechte, viewFilters, { showAll, scopeUsers }),
        [uniqueProjects, userProfile, rechte, viewFilters, showAll, scopeUsers]
    );

    // 2b. Zahl am Chip „Alle": so viele Projekte zeigt die Liste nach dem
    // Umschalten unter dem aktuellen Status-Chip (ohne Suche — wie die
    // Zahlen an den Status-Chips). `null` = noch nicht bekannt (ohne
    // geladene Benutzerliste) oder kein Recht auf „Alle".
    const allScopeCount = useMemo(() => {
        const scope = selectAllScope(uniqueProjects, userProfile, rechte, scopeUsers);
        return scope ? scope.filter(p => matchesStatusFilter(p, statusFilter)).length : null;
    }, [uniqueProjects, userProfile, rechte, scopeUsers, statusFilter]);

    // „Alle" gewählt, aber der Bereich ist (noch) nicht bestimmbar — die
    // Liste zeigt solange die persönliche Auswahl.
    const allScopePending = resolveShowAll(showAll, rechte, userProfile?.role) && allScopeCount === null;

    // 3. Process (Search, Status, Sort)
    const processedProjects = useMemo(() => {
        let results = relevantProjects;

        if (searchTerm) {
            const lower = searchTerm.toLowerCase().trim();
            results = results.filter((p) => {
                const meta = p.data?.metadata;
                return (p.name || '').toLowerCase().includes(lower) ||
                    (p.project_number || '').toLowerCase().includes(lower) ||
                    (meta?.projectNumber || '').toLowerCase().includes(lower) ||
                    (meta?.projectName || '').toLowerCase().includes(lower) ||
                    (meta?.projectAddress || '').toLowerCase().includes(lower);
            });
        }

        // 'active_all' — alles außer Archiv; dieselbe Regel wie die Zahl am Chip „Alle".
        if (statusFilter !== 'all') {
            results = results.filter((p) => matchesStatusFilter(p, statusFilter));
        }

        const parseDateSort = (d?: string) => {
            if (!d) return 8640000000000000;
            if (d.includes('.') && d.split('.').length === 3) {
                const parts = d.split('.');
                if (!isNaN(Number(parts[0])) && !isNaN(Number(parts[1])) && !isNaN(Number(parts[2]))) {
                    return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).getTime();
                }
            }
            const ts = new Date(d).getTime();
            return isNaN(ts) ? 8640000000000000 : ts;
        };

        // Kopie sortieren: bei „Alle" (super_admin) und der Arbeitsvorbereitung im Rückfall ist
        // `results` sonst dasselbe Array wie die entdoppelte Liste.
        return [...results].sort((a, b) => {
            const metaA = a.data.metadata;
            const metaB = b.data.metadata;
            let valA: any = '', valB: any = '';
            switch (sortConfig.key) {
                case 'projectNumber': valA = a.project_number || metaA.projectNumber || ''; valB = b.project_number || metaB.projectNumber || ''; break;
                case 'date': valA = parseDateSort(a.last_inspection_date || metaA.date); valB = parseDateSort(b.last_inspection_date || metaB.date); break;
                case 'updated_at': valA = new Date(a.updated_at).getTime(); valB = new Date(b.updated_at).getTime(); break;
                case 'status':
                    const so = { planning: 3, active: 2, on_hold: 1, finished: 0 };
                    valA = so[(a.status || metaA.globalData?.status || 'planning') as keyof typeof so] || 0;
                    valB = so[(b.status || metaB.globalData?.status || 'planning') as keyof typeof so] || 0;
                    break;
                default: return 0;
            }
            if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
            if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [relevantProjects, searchTerm, statusFilter, sortConfig]);

    // 4. Counts
    const counts = useMemo(() => ({
        all: relevantProjects.length,
        active: relevantProjects.filter((p) => (p.status || p.data.metadata?.globalData?.status) === 'active').length,
        planning: relevantProjects.filter((p) => (p.status || p.data.metadata?.globalData?.status) === 'planning').length,
        on_hold: relevantProjects.filter((p) => (p.status || p.data.metadata?.globalData?.status) === 'on_hold').length,
        completed: relevantProjects.filter((p) => (p.status || p.data.metadata?.globalData?.status) === 'completed').length,
        finished: relevantProjects.filter((p) => (p.status || p.data.metadata?.globalData?.status) === 'finished').length,
    }), [relevantProjects]);

    const activeSubstitutionsCount = useMemo(() => {
        const myEmail = (userProfile?.email || '').trim().toLowerCase();
        if (!myEmail) return 0;
        return relevantProjects.filter((p) => isUserActiveSubstitute(p.data?.metadata, myEmail)).length;
    }, [relevantProjects, userProfile]);

    return { processedProjects, counts, activeSubstitutionsCount, ownCount: relevantProjects.length, allScopeCount, allScopePending };
};